import React, { useState, useEffect } from "react";
import dateTimeUtils from "../../../../utils/date-utils";
import TodayFollowupGridComponent from "./TodayFollowupGridComponent";
import { PagingEnum } from "../../../../models/enums/IAIEnum";
import { SortDirection } from "../../../../models/enums/SortDirectionEnum";
import modalUtils from "../../../../utils/modal-utils";
import { GeneralPoupConfig, ModalSize } from "../../../../models/modal-types";
import TodayFollowupPopup from "../../candidate/today-followups/TodayFollowupUpdateComponent"
import IdNameModel from "../../../../models/common/IdNameModel";
import PagedListModel from "../../../../models/common/PagedListModel";
import CandidateStatusModel from "../../../../models/admin/candidate/response/CandidateStatusModel";
import masterDataService from "../../../../services/master-data-service";
import toasterUtils from "../../../../utils/toaster-utils";
import CandidateStatusSearchInputModel from "../../../../models/admin/candidate/request/CandidateStatusSearchInputModel";
import loaderUtils from "../../../../utils/loader-utils";
import adminCandidateService from "../../../../services/admin/admin-candidate-service";
import CanidateStatusRequestModel from "../../../../models/admin/candidate/request/CanidateStatusRequestModel";
import CandidateHistoryPopup from "../candidate-history/CandidateHistoryComponent";
import { Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

interface TodayFollowupProps {
    statusList: IdNameModel[];
};

const TodayFollowupComponent = (props: TodayFollowupProps) => {
    const [candidatePagedList, setCandidatePagedList] = useState<PagedListModel<CandidateStatusModel>>();
    const [candidateList, setCandidateList] = useState<CandidateStatusModel[]>([]);
    const [refreshCandidate, setRefreshCandidate] = useState<number>(2);
    const [startDateSearch, setStartDateSearch] = useState<Dayjs | null>(dayjs());
    const [endDateSearch, setEndDateSearch] = useState<Dayjs | null>(dayjs());
    const [CandidateNameFilter, setCandidateNameFilter] = useState<string>("");
    const [page, setPage] = useState<number>(PagingEnum.page);
    const [pageSize, setPageSize] = useState<number>(PagingEnum.pageSize);
    const [sortColumn, setSortColumn] = useState<string>("FollowUpDate");
    const [sortDirection, setSortDirection] = useState<number>(PagingEnum.sortDirection);
    const [pageSizesList, setPageSizesList] = useState<number[]>([]);

    useEffect(() => {
        loadPageSizes();
        getCandidates(getSearchFilters(CandidateNameFilter, page, pageSize, sortColumn, sortDirection));
    }, []);

    const loadPageSizes = () => {
        masterDataService.loadPageSizesForPaging().then((res) => {
            setPageSizesList(res);
        }).catch((error) => {
            toasterUtils.showError(error);
        });
    };

    const getSearchFilters = (nameFilter: string, page: number, pageSize: number, sortColumn: string, sortDirection: number) => {
        var inputData: CandidateStatusSearchInputModel = {
            startDate: dateTimeUtils.getFormattedDate(startDateSearch ? startDateSearch.toDate() : new Date()),
            endDate: dateTimeUtils.getFormattedDate(endDateSearch ? endDateSearch.toDate() : new Date()),
            candidateName: nameFilter,
            statusId: 0,
            candidateId: null,
            page: page,
            pageSize: pageSize,
            sort: sortColumn,
            sortDir: sortDirection
        };
        return inputData;
    };

    const getCandidates = (inputData: CandidateStatusSearchInputModel) => {
        setRefreshCandidate(2);
        modalUtils.showLoader();
        adminCandidateService.getFollowUps(inputData).then((res) => {
            modalUtils.closeModal();
            setCandidateList([]);
            setCandidatePagedList(res);
            setCandidateList(res.items);
            setRefreshCandidate(refreshCandidate * 5);
        }).catch((error) => {
            setRefreshCandidate(refreshCandidate * 5);
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    const onUpdateFollowupActionHandler = (objCandidate: CanidateStatusRequestModel) => {
        const config: GeneralPoupConfig = {
            title: "update Status",
            content: (
                <TodayFollowupPopup
                    objCandidate={objCandidate}
                    statusList={props.statusList}
                    onFollowupClose={onFollowupCloseHandler}
                    onFollowupUpdate={onFollowUpUpdateHandler}
                />
            ),
            size: ModalSize.LG,
            className: "model-min-height-500"
        };
        modalUtils.showPopup(config);
    };

    const onFollowupCloseHandler = () => {
        modalUtils.closeModal();
    };

    const onFollowUpUpdateHandler = (objCandidate: CanidateStatusRequestModel) => {
        setRefreshCandidate(2);
        modalUtils.showLoader();
        adminCandidateService.saveCandidateStatus(objCandidate).then((res) => {
            modalUtils.closeModal();
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            toasterUtils.showSuccess(res.informationMessages.toString());
            getCandidates(getSearchFilters(CandidateNameFilter, page, pageSize, sortColumn, sortDirection));
        }).catch((error) => {
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    const onCandidateViewHistoryHandler = (candidateId: string) => {
        const config: GeneralPoupConfig = {
            title: "Candidate History",
            content: (
                <CandidateHistoryPopup
                    candidateId={candidateId} />
            ),
            size: ModalSize.XL,
            className: "model-min-height-500"
        };
        modalUtils.showPopup(config);
    };

    const onPageChangeActionHandler = (newPage: number) => {
        setPage(newPage);
    };

    const onPageSizeChangeActionHandler = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(PagingEnum.page);
    };

    const onSortActionHandler = (newSortColumn: string, sortDir: string) => {
        setSortColumn(newSortColumn);
        setSortDirection(sortDir === "asc" ? SortDirection.Asc : SortDirection.Desc);
    };

    const onFilterActionHandler = (filterColumn: string, filterValue: string) => {
        setPage(0);
        setCandidateNameFilter(filterValue);
    };

    return (
        <div className="card card-white">
            <div className="card-body">
                <div className="row">
                    <div className="col-md-2">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Start Date"
                                value={startDateSearch}
                                onChange={(newValue) => setStartDateSearch(newValue)}
                                format="YYYY-MMM-DD"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: "small",
                                        margin: "normal",
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </div>
                    <div className="col-md-2">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="End Date"
                                value={endDateSearch}
                                onChange={(newValue) => setEndDateSearch(newValue)}
                                format="YYYY-MMM-DD"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: "small",
                                        margin: "normal",
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </div>
                    <div className="col-md-2">
                        <Button variant="contained" color="success" onClick={() => getCandidates(getSearchFilters(CandidateNameFilter, page, pageSize, sortColumn, sortDirection))} startIcon={<SearchIcon />} sx={{ ml: 2, mt: 2 }}>
                            Search
                        </Button>
                    </div>
                </div>
                <div className="mt-4">
                    <TodayFollowupGridComponent
                        followupList={candidateList}
                        totalSize={candidatePagedList?.totalCount ? candidatePagedList?.totalCount : candidateList.length}
                        currentPage={page + 1}
                        pagesize={pageSize}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection === SortDirection.Asc ? "asc" : "desc"}
                        pageSizesList={pageSizesList}
                        candidateNameFilterValue={CandidateNameFilter}
                        onUpdateFollowupAction={onUpdateFollowupActionHandler}
                        onCandidateViewHistory={onCandidateViewHistoryHandler}
                        onPageChangeAction={onPageChangeActionHandler}
                        onPageSizeChangeAction={onPageSizeChangeActionHandler}
                        onSortAction={onSortActionHandler}
                        onFilterAction={onFilterActionHandler} />
                </div>
            </div>
        </div>
    );
}

export default TodayFollowupComponent;
